import { useQuery } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api-client";
import type { UserMeData } from "@/hooks/queries/current-user-types";

// Story 2.12 — GET /auth/me identity hook. The JWT deliberately carries no
// first_name/last_name (PHI-free claims), so any surface that shows the
// clinician's NAME must read it from this endpoint; auth-context keeps
// owning session identity (id/email/role from the token).
// `skipAuthRefresh` is deliberately OMITTED: post-session call, rides the
// silent-refresh path.

/** Root key for the current-user query — `invalidateQueries({ queryKey: [CURRENT_USER_QUERY_KEY] })`. */
export const CURRENT_USER_QUERY_KEY = "current-user";

/** Stable, fully-resolved query key (single-object query — no params today). */
export function currentUserQueryKey() {
  return [CURRENT_USER_QUERY_KEY] as const;
}

export function useCurrentUser() {
  return useQuery({
    queryKey: currentUserQueryKey(),
    queryFn: () => apiFetch<UserMeData>("/auth/me"),
    // Identity changes rarely; avoid refetching on every Review-step mount.
    staleTime: 5 * 60 * 1000,
    // Offline-aware settle (gh-471 posture): under the default
    // `networkMode: "online"` React Query would PAUSE this query offline and the
    // Clinician row would wait forever. `"always"` lets the transport error settle
    // to `isError` fast, and `retry: false` avoids 3× churn — the consumer then
    // falls back to the auth-context email. NO IDB fallback: the fallback IS the
    // email already available from the session.
    networkMode: "always",
    retry: false,
  });
}
